import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { useHayServidor } from '../hooks/useHayServidor';

const Fondo = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.colors.bgPrincipal};
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Tarjeta = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 28px;
  width: 100%;
  max-width: 440px;
  box-shadow: ${({ theme }) => theme.colors.shadowModal};
`;

const Titulo = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  margin: 0 0 6px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Aviso = styled.p`
  font-size: 0.88rem;
  color: ${({ theme }) => theme.colors.txtSecondary};
  margin: 0 0 20px;
  line-height: 1.5;
`;

const Field = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.txtSecondary};
  margin-bottom: 5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${({ theme, $error }) => ($error ? theme.colors.danger : theme.colors.borderDefault)};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.bgSecundario};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 0.9rem;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryAlpha};
  }
`;

const ErrorMsg = styled.div`
  background: ${({ theme }) => theme.colors.dangerBg};
  border: 1px solid ${({ theme }) => theme.colors.dangerBorder};
  color: ${({ theme }) => theme.colors.dangerTxt};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 8px 12px;
  font-size: 0.83rem;
  margin-bottom: 14px;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;
`;

const BtnPrimary = styled.button`
  flex: 1;
  padding: 10px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.9rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  min-height: 40px;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: 0.88; }
`;

/* Cambiar la contraseña se valida contra el servidor: sin servidor no hay nada
   que este formulario pueda hacer, y conviene decirlo antes de que alguien lo
   complete tres veces creyendo que se equivoca de contraseña. */
const AvisoSinServidor = styled.div`
  padding: 12px 14px;
  margin: 0 0 16px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.warningBg};
  color: ${({ theme }) => theme.colors.warningTxt};
  border: 1px solid ${({ theme }) => theme.colors.warningBorder};
  font-size: 0.83rem;
  line-height: 1.45;
`;

const BtnSalir = styled.button`
  padding: 10px 16px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 0.9rem;
  font-family: inherit;
  cursor: pointer;
  min-height: 40px;

  &:hover { background: ${({ theme }) => theme.colors.bgSecundario}; }
`;

/**
 * Pantalla de cambio de contraseña OBLIGATORIO. Se muestra a pantalla completa
 * cuando el usuario tiene debe_cambiar_password activo (admin inicial creado con
 * credencial por defecto). No hay forma de saltearla: solo cambiar la contraseña
 * o cerrar sesión.
 */
export default function CambioPasswordObligatorio() {
  const { changePassword, logout } = useAuth();
  const { hayServidor } = useHayServidor();
  // En la app instalada este gate tapa también las pantallas que funcionan sin
  // sesión —el parte diario lo carga cualquiera, incluso sin cuenta—, así que
  // ahí "cerrar sesión" no es rendirse: es la forma de seguir trabajando.
  const enAppMovil = useLocation().pathname.startsWith('/app');
  const [form, setForm] = useState({ old_password: '', new_password: '', new_password_confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.old_password || !form.new_password || !form.new_password_confirm) {
      setError('Completá todos los campos.');
      return;
    }
    if (form.new_password !== form.new_password_confirm) {
      setError('Las contraseñas nuevas no coinciden.');
      return;
    }
    if (form.new_password === form.old_password) {
      setError('La nueva contraseña debe ser distinta a la actual.');
      return;
    }
    setLoading(true);
    // changePassword del hook limpia debe_cambiar_password al tener éxito,
    // lo que hace desaparecer este gate automáticamente.
    const result = await changePassword({
      old_password: form.old_password,
      new_password: form.new_password,
      new_password_confirm: form.new_password_confirm,
    });
    setLoading(false);
    if (!result.success) {
      setError(result.error || 'No se pudo cambiar la contraseña. Verificá los datos.');
    }
  };

  return (
    <Fondo>
      <Tarjeta>
        <Titulo>
          <i className="bi bi-shield-lock" />
          Cambiá tu contraseña para continuar
        </Titulo>
        <Aviso>
          Estás usando una contraseña inicial por defecto. Por seguridad, definí una
          contraseña propia antes de seguir usando el sistema.
        </Aviso>
        {!hayServidor && (
          <AvisoSinServidor>
            Ahora mismo no se llega al servidor, y la contraseña se cambia contra
            el servidor: hasta que vuelva la conexión este formulario no va a poder
            guardarla.
            {enAppMovil && ' Mientras tanto podés tocar «Cerrar sesión» y seguir '
              + 'cargando partes diarios en la obra, que no necesitan cuenta.'}
          </AvisoSinServidor>
        )}
        <form onSubmit={handleSubmit}>
          {error && <ErrorMsg>{error}</ErrorMsg>}
          <Field>
            <Label htmlFor="op">Contraseña actual</Label>
            <Input
              id="op"
              name="old_password"
              type="password"
              value={form.old_password}
              onChange={handleChange}
              autoComplete="current-password"
              $error={!!error && error.toLowerCase().includes('actual')}
            />
          </Field>
          <Field>
            <Label htmlFor="np">Nueva contraseña</Label>
            <Input
              id="np"
              name="new_password"
              type="password"
              value={form.new_password}
              onChange={handleChange}
              autoComplete="new-password"
            />
          </Field>
          <Field>
            <Label htmlFor="npc">Repetir nueva contraseña</Label>
            <Input
              id="npc"
              name="new_password_confirm"
              type="password"
              value={form.new_password_confirm}
              onChange={handleChange}
              autoComplete="new-password"
            />
          </Field>
          <BtnRow>
            <BtnSalir type="button" onClick={logout}>
              {enAppMovil ? 'Cerrar sesión y seguir en la app' : 'Cerrar sesión'}
            </BtnSalir>
            <BtnPrimary type="submit" disabled={loading || !hayServidor}>
              {loading ? 'Guardando...' : 'Cambiar y continuar'}
            </BtnPrimary>
          </BtnRow>
        </form>
      </Tarjeta>
    </Fondo>
  );
}
